import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { Calculator, Scale } from 'lucide-react';
import { useState } from 'react';

import {
  CARBON_CLAIMS,
  CLAIM_VERDICT_LABELS,
  EMISSIONS_CASES,
  EMISSIONS_UNITS,
  checkClaimVerdict,
  checkEmissionsAnswer,
  emissionsPrompt,
  solveEmissionsCase,
  type CarbonClaim,
  type ClaimVerdict,
  type EmissionsCase,
} from './fuelsSustainability';
import { FuelsEmissionsExplore } from './FuelsEmissionsExplore';
import { CaseList, FeedbackPanel, OptionButton } from './FuelsSustainabilityShared';
import type { AnswerCheck } from './gasLaws';

const VERDICT_OPTIONS: ClaimVerdict[] = ['supported', 'not-supported', 'depends'];

function CalculationPanel() {
  const [selected, setSelected] = useState<EmissionsCase>(EMISSIONS_CASES[0]);
  const [answer, setAnswer] = useState('');
  useScienceTutorSection('CalculationPanel', { selected: tutorSelection(selected), answer });
  const [check, setCheck] = useState<AnswerCheck | null>(null);
  const solution = check ? solveEmissionsCase(selected) : null;

  const choose = (id: string) => {
    const next = EMISSIONS_CASES.find((entry) => entry.id === id);
    if (!next) return;
    setSelected(next);
    setAnswer('');
    setCheck(null);
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)]">
      <CaseList
        heading="Practice · choose a calculation"
        label="Emissions calculations"
        items={EMISSIONS_CASES.map((entry) => ({
          id: entry.id,
          label: entry.title,
          detail: entry.question === 'co2-mass' ? 'Mass of CO₂' : 'CO₂ per MJ',
        }))}
        selectedId={selected.id}
        onSelect={choose}
      />
      <section className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
          <Calculator className="h-4 w-4" aria-hidden="true" /> Stoichiometric CO₂
        </p>
        <p className="mt-2 text-base font-bold leading-7 text-ink">{emissionsPrompt(selected)}</p>
        <form
          className="mt-4 flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            setCheck(checkEmissionsAnswer(selected, answer));
          }}
        >
          <label className="grid gap-1 text-sm font-extrabold text-ink">
            Your answer ({EMISSIONS_UNITS[selected.question]})
            <input
              value={answer}
              onChange={(event) => {
                setAnswer(event.target.value);
                setCheck(null);
              }}
              inputMode="decimal"
              aria-label={`CO₂ answer in ${EMISSIONS_UNITS[selected.question]}`}
              className="w-40 rounded-2xl border border-hairline bg-surface px-3 py-2 text-sm font-bold text-ink"
            />
          </label>
          <button type="submit" className="btn-pill-primary">
            Check CO₂
          </button>
        </form>
        {check ? (
          <div className="mt-5 grid gap-4">
            <FeedbackPanel good={check.status === 'correct'} title={check.title} label="CO₂ feedback">
              <p>{check.feedback}</p>
            </FeedbackPanel>
            {solution && check.status !== 'invalid' ? (
              <ol className="list-decimal space-y-1 rounded-2xl border border-hairline bg-surface p-4 pl-8 text-sm font-medium leading-6 text-slate2">
                {solution.working.map((line) => (
                  <li key={line} className="break-words">
                    {line}
                  </li>
                ))}
              </ol>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function ClaimCard({ entry }: { entry: CarbonClaim }) {
  const [verdict, setVerdict] = useState<ClaimVerdict | null>(null);
  useScienceTutorSection('ClaimCard', { verdict });
  const check = verdict ? checkClaimVerdict(entry, verdict) : null;
  return (
    <li className="min-w-0 rounded-2xl border border-hairline bg-surface p-4">
      <p className="text-sm font-extrabold leading-6 text-ink">“{entry.claim}”</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-3" role="group" aria-label={`Verdict for: ${entry.claim}`}>
        {VERDICT_OPTIONS.map((option) => (
          <OptionButton key={option} pressed={verdict === option} onClick={() => setVerdict(option)}>
            {CLAIM_VERDICT_LABELS[option]}
          </OptionButton>
        ))}
      </div>
      {check ? (
        <div className="mt-3">
          <FeedbackPanel good={check.status === 'correct'} title={check.title} label="Claim feedback">
            <p>{check.feedback}</p>
          </FeedbackPanel>
        </div>
      ) : null}
    </li>
  );
}

export function FuelsSustainabilityEmissions() {
  return (
    <div className="grid gap-6">
      <FuelsEmissionsExplore />
      <CalculationPanel />
      <section
        className="min-w-0 rounded-[1.75rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6"
        aria-labelledby="carbon-claims-heading"
      >
        <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
          <Scale className="h-4 w-4" aria-hidden="true" /> Evidence boundaries
        </p>
        <h3 id="carbon-claims-heading" className="mt-1 text-xl font-extrabold text-ink">
          Test the carbon-neutral argument
        </h3>
        <p className="mt-1 text-sm font-medium leading-6 text-slate2">
          Decide whether the chemistry supports each claim, contradicts it, or needs assumptions
          that a combustion equation cannot test.
        </p>
        <ul className="mt-4 grid gap-3 lg:grid-cols-2">
          {CARBON_CLAIMS.map((entry) => (
            <ClaimCard key={entry.id} entry={entry} />
          ))}
        </ul>
      </section>
    </div>
  );
}
